import { omit } from 'lodash'
import keys from 'constants/keys'
import { EditorContent } from 'components/CommonComponents/RichTextEditor/types'
import { isEditorContentEmpty, textToEditorContent } from 'components/CommonComponents/RichTextEditor/utils'
import { TaskFormData } from './types'

/** Normalize legacy media/description drafts and RHF v5 object-based poll options without mutating the draft. */
export function migrateSubtaskDefaults(defaultValues: TaskFormData['SUBTASK_TASK_CREATION']) {
  if (!defaultValues.sub_tasks) return defaultValues

  const migratedSubtasks = defaultValues.sub_tasks.map((subtask: any) => {
    // ── Step 1: Image / Video legacy format ──────────────────────────────
    // Old format had description/images/videos on the top level (no content object).
    // Move them into content.
    let normalized = subtask

    if (
      (subtask.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || subtask.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS) &&
      !subtask.content &&
      (subtask.description || subtask.images || subtask.videos)
    ) {
      const content: {
        text?: string
        rich_text?: EditorContent | null
        images?: string[]
        videos?: string[]
        files?: string[]
      } = {}

      if (subtask.description) content.text = subtask.description
      if (subtask.images?.length) content.images = subtask.images
      if (subtask.videos?.length) content.videos = subtask.videos
      if (subtask.files?.length) content.files = subtask.files
      // support old camelCase key that may have been saved previously
      if (subtask.richText) content.rich_text = subtask.richText

      normalized = { ...omit(subtask, ['description', 'images', 'videos', 'files', 'has_description']), content }
    }

    // ── Step 2: Content items (description blocks) ───────────────────────
    // If text exists and rich_text is absent, convert text → rich_text and
    // clear text so the form always works with rich_text only.
    if ('index_ref' in normalized) {
      const hasText = !!normalized.text?.trim()
      const hasRichText = !isEditorContentEmpty(normalized.rich_text)
      if (hasText && !hasRichText) {
        return {
          ...normalized,
          rich_text: textToEditorContent(normalized.text),
          text: null,
        }
      }
      return normalized
    }

    // ── Step 3: Image / Video subtask content ────────────────────────────
    // Same rule for content.text → content.rich_text.
    if (
      (normalized.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || normalized.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS) &&
      normalized.content
    ) {
      const hasContentText = !!normalized.content.text?.trim()
      const hasContentRichText = !isEditorContentEmpty(normalized.content.rich_text)
      if (hasContentText && !hasContentRichText) {
        return {
          ...normalized,
          content: {
            ...normalized.content,
            rich_text: textToEditorContent(normalized.content.text),
            text: null,
          },
        }
      }
    }

    if (
      (normalized.type === keys.SUBTASK_TYPES.POLL_SUBTASK || normalized.type === keys.SUBTASK_TYPES.PRIVATE_POLL) &&
      Array.isArray(normalized.options)
    ) {
      return {
        ...normalized,
        options: normalized.options.map((option: string | { value?: string }) =>
          typeof option === 'string' ? { value: option } : { value: option.value ?? '' }
        ),
      }
    }

    return normalized
  })

  return {
    ...defaultValues,
    sub_tasks: migratedSubtasks,
  }
}
